import { DataError } from "../../core/errors/DataError";
import { CatalogUploadIssue } from "../../core/models/entities/ErrorStatus";

export type { CatalogUploadIssue };

// POST styles/upload/{manufacturer} (admin catalog upload) answers 400 while the
// API's CatalogUploadValidationEnabled is on and the file has problems:
// { error: "invalidCatalog", message, total, truncated, errors: [{ sheet, row,
// ean, column, message }] } (CatalogValidationResult.ToHttpBody in the API;
// row/ean/column are null when they do not apply). This module is the pure,
// DOM-free part of the frontend flag catalogUploadErrorsEnabled: a bounded,
// item-by-item validated reading of that body, and the text the copy button
// hands over. Nothing here reads the flag - mapError and CatalogUpload do.
export const CATALOG_UPLOAD_INVALID_ERROR_CODE = "invalidCatalog";

// The API puts at most 100 issues on the wire (CatalogValidationResult.
// MaxReportedIssues) and stops at 1,000 problems per sheet (CatalogUploadValidator.
// MaxIssuesPerSheet). Whatever a body claims, never keep more than this many, so
// the panel and the copy text always have a bounded list to work with.
export const MAX_PARSED_CATALOG_ISSUES = 1000;

// Rows the panel renders; the remainder is one "and N more" line (the copy
// button copies everything parsed, not only the visible rows).
export const MAX_VISIBLE_CATALOG_ISSUES = 100;

// A `total` above this is not a count the API can produce (1,000 per sheet) -
// treated like a missing total, so the "and N more" line never shows nonsense.
const MAX_CLAIMED_TOTAL = 1_000_000;

export interface CatalogUploadRejection {
  // The body's own `message` (the count + the first problems spelled out) -
  // for the copy text; "" when the body has none. Never a fallback such as the
  // marker or the raw body text.
  message: string;
  issues: CatalogUploadIssue[];
  // The body's total when it is at least the issue count, otherwise the issue
  // count. total > issues.length = the API cut its list (its `truncated`
  // marker says the same and is not read).
  total: number;
}

function nonBlank(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() !== "" ? value : undefined;
}

// Excel rows are 1-based (the heading is row 1); 0, negatives, fractions and
// unsafe numbers are dropped like a missing row.
function rowOf(value: unknown): number | undefined {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 1
    ? value
    : undefined;
}

function toIssue(raw: unknown): CatalogUploadIssue | undefined {
  if (raw === null || typeof raw !== "object") return undefined;
  const item = raw as Record<string, unknown>;
  const message = nonBlank(item.message);
  if (!message) return undefined;
  const sheet = nonBlank(item.sheet);
  const ean = nonBlank(item.ean);
  const column = nonBlank(item.column);
  const row = rowOf(item.row);
  return {
    message,
    ...(sheet !== undefined && { sheet }),
    ...(row !== undefined && { row }),
    ...(ean !== undefined && { ean }),
    ...(column !== undefined && { column }),
  };
}

// The valid items of a body's errors[] (at most MAX_PARSED_CATALOG_ISSUES), or
// undefined when there is no array or no item with a message.
export function parseCatalogUploadIssues(
  raw: unknown,
): CatalogUploadIssue[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const issues = raw
    .slice(0, MAX_PARSED_CATALOG_ISSUES * 2)
    .map(toIssue)
    .filter((issue): issue is CatalogUploadIssue => issue !== undefined)
    .slice(0, MAX_PARSED_CATALOG_ISSUES);
  return issues.length > 0 ? issues : undefined;
}

function totalOf(claimed: unknown, issueCount: number): number {
  const usable =
    typeof claimed === "number" &&
    Number.isSafeInteger(claimed) &&
    claimed <= MAX_CLAIMED_TOTAL
      ? claimed
      : 0;
  return Math.max(usable, issueCount);
}

// The rejection a parsed 400 body describes, or undefined when the body has no
// usable errors[] (a plain string body, an envelope without the list, ...).
// The caller (mapError) checks the "invalidCatalog" marker; this only reads
// the shape.
export function catalogUploadRejectionFromBody(
  body: unknown,
): CatalogUploadRejection | undefined {
  if (body === null || typeof body !== "object") return undefined;
  const raw = body as Record<string, unknown>;
  const issues = parseCatalogUploadIssues(raw.errors);
  if (!issues) return undefined;
  return {
    message: nonBlank(raw.message) ?? "",
    issues,
    total: totalOf(raw.total, issues.length),
  };
}

// The rejection a thrown upload error carries, or undefined for every other
// error - those keep today's toast. mapError puts the parsed issues on the
// DataError's ErrorStatus only for a 400 with the "invalidCatalog" marker and
// a usable errors[] while the flag is on, so the issues' presence IS the
// discriminant here (ErrorStatus.code may be the body's numeric `code`, not
// the HTTP status, and is not consulted).
export function catalogUploadRejectionFromError(
  error: unknown,
): CatalogUploadRejection | undefined {
  if (!(error instanceof DataError)) return undefined;
  const status = error.errorStatus;
  const issues = status?.catalogIssues;
  if (!status || !issues || issues.length === 0) return undefined;
  return {
    // The body's own message only (mapError fills ErrorStatus.message with the
    // marker or the raw body text when the body has none - never shown here).
    message: status.catalogIssueMessage ?? "",
    issues,
    total: totalOf(status.catalogIssueTotal, issues.length),
  };
}

// "Styles row 3 (EAN 5701234567890) [Width]: Width is missing" - the API's own
// log line (CatalogValidationResult.Describe) plus the column, for the copy text.
export function describeCatalogUploadIssue(issue: CatalogUploadIssue): string {
  const where = [
    issue.sheet,
    issue.row !== undefined ? `row ${issue.row}` : undefined,
    issue.ean ? `(EAN ${issue.ean})` : undefined,
    issue.column ? `[${issue.column}]` : undefined,
  ]
    .filter((part): part is string => part !== undefined)
    .join(" ");
  return where ? `${where}: ${issue.message}` : issue.message;
}

// What the copy button hands to the clipboard: the message, a blank line, one
// line per issue received and, when the API reported more than it sent, how
// many are missing. English on purpose - it is pasted into a mail to the vendor
// or an issue, and the API's own texts are English.
export function catalogUploadRejectionToText(
  rejection: CatalogUploadRejection,
): string {
  const hidden = rejection.total - rejection.issues.length;
  return [
    ...(rejection.message !== "" ? [rejection.message, ""] : []),
    ...rejection.issues.map(describeCatalogUploadIssue),
    ...(hidden > 0 ? [`(+ ${hidden} more not included in this list)`] : []),
  ].join("\n");
}
